import { useState } from "react";

import { omit } from "lodash";
import { useToasts } from "react-toast-notifications";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { type SavedView } from "@features/metrics-explorer/types";
import {
  GET_METRICS_EXPLORER_SAVED_VIEWS,
  UPDATE_METRICS_EXPLORER_SAVED_VIEW,
} from "@gql/metrics-explorer-saved-views";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type UpdateMetricsExplorerSavedView,
  type UpdateMetricsExplorerSavedViewVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

interface Props {
  savedView: SavedView;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const UpdateSavedViewsModal = ({
  savedView,
  isOpen,
  onConfirm,
  onCancel,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const { filters, tiles, chartState } = useMetricsExplorerContext();

  const [isSaving, setIsSaving] = useState(false);
  const toast = useToasts();

  const [updateSavedView] = useNorthbeamMutation<
    UpdateMetricsExplorerSavedView,
    UpdateMetricsExplorerSavedViewVariables
  >(UPDATE_METRICS_EXPLORER_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_METRICS_EXPLORER_SAVED_VIEWS }],
  });

  const saveSavedView = async () => {
    prettyLogEventPage({
      domain: "Metrics Explorer",
      action: "onChange",
      additionalActionInfo: "Update Saved View",
    });

    try {
      setIsSaving(true);
      await updateSavedView({
        variables: {
          id: savedView.id,
          config: {
            filters: omit(filters, ["dateRangeInterval"]),
            tiles,
            chartState,
          },
        },
      });
      onConfirm();
      toast.addToast("Saved view updated", { appearance: "success" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      headerTitleProps={{ className: "text-xl font-semibold" }}
      titleContent={
        <div>Overwrite current view for all users on your account?</div>
      }
    >
      <p className="text-sm mb-8 text-[#717680]">Changes cannot be undone</p>
      <div className="flex space-x-3">
        <PrimaryButton loading={isSaving} onClick={saveSavedView}>
          YES, SAVE
        </PrimaryButton>
        <SecondaryButton disabled={isSaving} onClick={onCancel}>
          NO, CANCEL
        </SecondaryButton>
      </div>
    </Modal>
  );
};
